/**
 * TarihGirdisi.jsx — gün/ay/yıl tarih alanı (backlog #560).
 *
 * Yerel `<input type="date">` tarihi tarayıcının / işletim sisteminin dilinde gösterir (İngilizce Chrome'da aa/gg/yyyy) ve hiçbir
 * öznitelik bunu değiştiremez. Burada gösterilen ve yazılan biçim HER ZAMAN gg/aa/yyyy; dışarı verilen değer yine yyyy-aa-gg, yani
 * API sözleşmesi ve çağıranların karşılaştırmaları (`tarih < bugunYmd()`) değişmez.
 *
 * `onChange(ymd)` — yerel olay değil, doğrudan "yyyy-aa-gg" metni gelir; boş ya da geçersiz yazı "" döner. Yazılan metin sıkı çözülür
 * (31/02/2026 → boş) ve 5.11.2026 / 05-11-2026 / 05112026 da kabul edilir. `min` / `max` yalnızca takvimde dışarıda kalan günleri
 * soluklaştırır; yazılan tarihi silmez (çağıranın `invalid` mesajı gösterebilsin diye, yerel `min` / `max` davranışı gibi).
 */
import React, { useEffect, useMemo, useRef } from "react";
import Flatpickr from "react-flatpickr";
import { Turkish } from "flatpickr/dist/l10n/tr.js";
import { Input } from "reactstrap";
import { useTranslation } from "react-i18next";

const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** Gün/ay/yıl sayıları gerçek bir takvim günü mü? (31 Şubat'ı bir sonraki aya taşımaz.) */
function gunOlustur(y, a, g) {
  const d = new Date(y, a - 1, g);
  return d.getFullYear() === y && d.getMonth() === a - 1 && d.getDate() === g ? d : undefined;
}

/** flatpickr'ın gevşek çözümleyicisi yerine: "d/m/Y" (yazılan) ve "Y-m-d" (değer) biçimlerini sıkı çözer; olmazsa undefined. */
export function tarihCoz(metin, bicim) {
  const s = String(metin ?? "").trim();
  if (bicim === "Y-m-d") {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    return m ? gunOlustur(+m[1], +m[2], +m[3]) : undefined;
  }
  const m = /^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/.exec(s) || /^(\d{2})(\d{2})(\d{4})$/.exec(s);
  return m ? gunOlustur(+m[3], +m[2], +m[1]) : undefined;
}

// `min` / `max`: yalnızca takvimde dışarıda kalan günleri soluklaştırır. `bsSize={null}` normal boy; `inputStyle` görünür kutuya biner.
// `yerTutucuMetni`: boş kutuda "gg/aa/yyyy" yerine gösterilecek soluk metin (#718: rolün boş bırakılan kurulumu fuarın kurulum tarihini gösterir).
export default function TarihGirdisi({ value = "", onChange, min, max, invalid = false, disabled = false, bsSize = "sm", id, className, style, inputStyle, yerTutucuMetni }) {
  const { t, i18n } = useTranslation("common");
  const yerTutucu = yerTutucuMetni || t("tarihYerTutucu");
  const fpRef = useRef(null);
  const tr = (i18n?.language || "tr").startsWith("tr");

  const secenekler = useMemo(() => ({
    altInput: true,
    altFormat: "d/m/Y",
    dateFormat: "Y-m-d",
    allowInput: true,
    disableMobile: true,
    locale: tr ? Turkish : { firstDayOfWeek: 1 },
    parseDate: tarihCoz,
    errorHandler: () => {},
    onDayCreate: (_dates, _str, _fp, gun) => {
      const g = ymd(gun.dateObj);
      if ((min && g < min) || (max && g > max)) gun.classList.add("flatpickr-disabled");
    },
  }), [tr, min, max]);

  // flatpickr'ın oluşturduğu görünür alan (altInput) React'ın bilmediği bir düğüm: sınıf, devre dışı, kimlik ve yer tutucu buradan eşlenir.
  useEffect(() => {
    const alan = fpRef.current?.altInput;
    if (!alan) return;
    alan.classList.toggle("is-invalid", !!invalid);
    alan.disabled = !!disabled;
    alan.placeholder = yerTutucu;
    if (id) alan.id = id;
    if (inputStyle) Object.assign(alan.style, inputStyle);
  }, [invalid, disabled, yerTutucu, id, inputStyle]);

  return (
    <div className={className} style={style}>
      <Flatpickr
        value={value || ""}
        options={secenekler}
        onCreate={(fp) => { fpRef.current = fp; }}
        onChange={(_dates, ymdMetin) => onChange?.(ymdMetin || "")}
        render={(_props, ref) => <Input innerRef={ref} bsSize={bsSize} autoComplete="off" disabled={disabled} placeholder={yerTutucu} />}
      />
    </div>
  );
}
